/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 10/15/2019
 *
 */

import React, {useState, useEffect} from 'react';
import './header.css';
import logo from '../../../assets/svg/pearsonIcon.svg';
import optionalIconsConfig from "./optionalIconsConfig";
import Icon from "../../generic/icon/Icon";
import EventEmitter from "../../../eventEmmiters/EventEmitter";

/** functional component for Header **/
const Header = (props) => {
    // all the data in header
    const [pageHeaderData, setPageHeaderData] = useState({});

    useEffect(() => {
        return() => {
            EventEmitter.off('setPageHeaderData', setData);
        }
    },[]);

    const setData = (dat) => {
        setPageHeaderData(dat);
    };

    // take data from 'setPageHeaderData' event emitter and set it
    EventEmitter.on('setPageHeaderData', setData);

    // initialize the optional icons in header
    const optionalIcons = [];

    // check whether the optional icons are there
    if (pageHeaderData.optionalIcons !== undefined) {
        pageHeaderData.optionalIcons.forEach(iconName => {
            // take suitable optional icon data from 'optionalIconsConfig.js'
            const optIcon = optionalIconsConfig[iconName];

            optionalIcons.push(
                <div key={optIcon.title} aria-label={optIcon.ariaLabel}
                        onClick={() => EventEmitter.emit(optIcon.emitterName, optIcon.emitterValue)}>
                    <i title={optIcon.title} className={optIcon.iconClassName}/>
                </div>
            );
        });
    }

    // initialize the main icons in header
    const mainIcons = [];

    // push home icon
    mainIcons.push(
        <a key={'Home'} href={process.env.REACT_APP_BASE_URL + "/#/home/"} aria-label="Home logo">
            <i title="Home" className="fa fa-lg fa-home"/>
        </a>
    );
    // push change pswrd icon
    mainIcons.push(
        <a key={'chngPswrd'} href={process.env.REACT_APP_BASE_URL + "/#/home/changeCredentials/"} aria-label="Change Pswrd logo">
            <i title="Change Credentials" className="fa fa-lg fa-key"/>
        </a>
    );
    // push sign out icon
    mainIcons.push(
        <a key={'Sign Out'} href={process.env.REACT_APP_BASE_URL + "/"} onClick={() => {localStorage.removeItem('tmcUser')}} aria-label="Sign Out logo" >
            <i title="Sign Out" className="fa fa-lg fa-sign-out"/>
        </a>
    );

    return(
        <div id="header">
            <div role="main" id='app-header' className="header">
                <div role="group" className="header-logo">
                    <div className="header-pearson-logo-container">
                        <a href="https://www.pearson.com/us/" aria-label="Pearson logo"
                           rel="noopener noreferrer"  target="_blank">
                            <img src={logo} height="37px" alt="Pearson Logo" />
                        </a>
                    </div>
                    <div className="header-vertical-line"/>
                    <div className="header-tmc-name-container">
                        <div className="header-app-name">
                            TPI Management Console <Icon name="forward-18"/>
                        </div>
                        <div className="header-page-name">
                            <a href={pageHeaderData.pageLink}>{pageHeaderData.pageName}</a>
                        </div>
                    </div>
                    <div className="header-icons">
                        <div style={{float: 'right'}} className="header-main-icons">
                            {mainIcons}
                        </div>
                        <div style={{float: 'right'}} className="header-optional-icons">
                            {optionalIcons}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default Header;
